import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import CloudIcon from "@mui/icons-material/Cloud";
import ComputerIcon from "@mui/icons-material/Computer";
import ScienceIcon from "@mui/icons-material/Science";
import HistoryIcon from "@mui/icons-material/History";
import BookmarksIcon from "@mui/icons-material/Bookmarks";
import FunctionsIcon from "@mui/icons-material/Functions";
import InsightsIcon from "@mui/icons-material/Insights";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { CLIENT_SEARCH_MODES } from "../services/queryEngineService";
import { LIVE_REFRESH_MODES } from "../services/liveRefreshService";

/**
 * Sprint 11 — Enterprise Query Engine control bar. Sits above the grid and
 * extends the Collection Explorer with the smart planner, server/client badge,
 * advanced client search, live refresh, and quick access to Explain / History /
 * Saved Views / Aggregate / Attendance Analytics.
 */
export default function QueryEngineBar({
  mode,
  serverActive,
  running,
  onRunQuery,
  onClearServer,
  liveModeId,
  onLiveModeChange,
  liveError,
  advanced,
  onAdvancedChange,
  fieldNames,
  isAttendance,
  onOpenExplain,
  onOpenHistory,
  onOpenViews,
  onOpenAggregate,
  onOpenAnalytics,
  missingIndex,
  metrics,
}) {
  const [term, setTerm] = useState(advanced?.term || "");

  useEffect(() => {
    setTerm(advanced?.term || "");
  }, [advanced?.term]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (term !== (advanced?.term || "")) onAdvancedChange({ ...advanced, term });
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  const liveMode = LIVE_REFRESH_MODES.find((m) => m.id === liveModeId) || LIVE_REFRESH_MODES[0];
  const isLive = liveMode.id !== "off";

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        <Tooltip title="Analyze current filters and run as a server-side Firestore query when possible">
          <span>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={running ? <CircularProgress size={16} color="inherit" /> : <BoltIcon />}
              onClick={onRunQuery}
              disabled={running}
            >
              Run Smart Query
            </Button>
          </span>
        </Tooltip>

        <Chip
          size="small"
          icon={mode === "server" ? <CloudIcon /> : <ComputerIcon />}
          color={mode === "server" ? "success" : "default"}
          variant={mode === "server" ? "filled" : "outlined"}
          label={mode === "server" ? "Server Query" : "Client Query"}
        />

        {serverActive && (
          <Button size="small" color="inherit" variant="text" onClick={onClearServer}>
            Exit server mode
          </Button>
        )}

        {isLive && (
          <Chip
            size="small"
            color="error"
            icon={<FiberManualRecordIcon sx={{ animation: "fa-pulse 1.4s infinite" }} />}
            label={liveMode.realtime ? "LIVE · Realtime" : `LIVE · ${liveMode.label}`}
          />
        )}

        <Box sx={{ flex: 1 }} />

        {metrics && (
          <Typography variant="caption" color="text.secondary">
            {metrics.docsReturned} docs · {metrics.queryTimeMs} ms
          </Typography>
        )}
      </Stack>

      <Divider sx={{ my: 1.25 }} />

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        {/* Advanced client search */}
        <TextField
          select
          size="small"
          label="Search field"
          value={advanced?.field || "__all__"}
          onChange={(e) => onAdvancedChange({ ...advanced, field: e.target.value })}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="__all__">All fields</MenuItem>
          {(fieldNames || []).map((f) => (
            <MenuItem key={f} value={f}>{f}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Match"
          value={advanced?.mode || "contains"}
          onChange={(e) => onAdvancedChange({ ...advanced, mode: e.target.value })}
          sx={{ minWidth: 150 }}
        >
          {CLIENT_SEARCH_MODES.map((m) => (
            <MenuItem key={m.id} value={m.id}>{m.label}</MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          placeholder="Advanced search (regex / soundex / fuzzy)…"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          sx={{ minWidth: 240, flex: 1 }}
        />

        <TextField
          select
          size="small"
          label="Live refresh"
          value={liveModeId || "off"}
          onChange={(e) => onLiveModeChange(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          {LIVE_REFRESH_MODES.map((m) => (
            <MenuItem key={m.id} value={m.id}>{m.label}</MenuItem>
          ))}
        </TextField>
      </Stack>

      <Divider sx={{ my: 1.25 }} />

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        <Button size="small" variant="outlined" color="inherit" startIcon={<ScienceIcon />} onClick={onOpenExplain}>
          Explain Query
        </Button>
        <Button size="small" variant="outlined" color="inherit" startIcon={<HistoryIcon />} onClick={onOpenHistory}>
          Query History
        </Button>
        <Button size="small" variant="outlined" color="inherit" startIcon={<BookmarksIcon />} onClick={onOpenViews}>
          Saved Views
        </Button>
        <Button size="small" variant="outlined" color="inherit" startIcon={<FunctionsIcon />} onClick={onOpenAggregate}>
          Aggregate
        </Button>
        {isAttendance && (
          <Button size="small" variant="outlined" color="secondary" startIcon={<InsightsIcon />} onClick={onOpenAnalytics}>
            Attendance Analytics
          </Button>
        )}
      </Stack>

      {missingIndex && (
        <Alert
          severity="warning"
          sx={{ mt: 1.25 }}
          action={
            missingIndex.consoleUrl ? (
              <Button color="inherit" size="small" href={missingIndex.consoleUrl} target="_blank" rel="noreferrer">
                Open Firebase Console
              </Button>
            ) : null
          }
        >
          Composite index required for this server query. Open the console to create it, then re-run.
        </Alert>
      )}

      {liveError && (
        <Alert severity="error" sx={{ mt: 1.25 }}>
          Live refresh error: {liveError}
        </Alert>
      )}

      <Box sx={{ "@keyframes fa-pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.2 } } }} />
    </Paper>
  );
}
